import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../../firebase';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    role: string | null;
    schoolId: string | null;
    boardId: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [role, setRole] = useState<string | null>(null);
    const [schoolId, setSchoolId] = useState<string | null>(null);
    const [boardId, setBoardId] = useState<string | null>(null);

    useEffect(() => {
        let previousUser: User | null = null;
        let callId = 0;

        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            const currentCallId = ++callId;
            
            if (currentUser) {
                try {
                    // GLOBAL PROFILE VALIDATION: Prevent access for users who bypassed signup
                    // Skip validation if they are actively in the signup flow to prevent race conditions
                    if (!sessionStorage.getItem('pp_signing_up')) {
                        const { doc, getDoc } = await import('firebase/firestore');
                        const userDocRef = doc(db, 'users', currentUser.uid);
                        const userDoc = await getDoc(userDocRef);
                        
                        if (currentCallId !== callId) return;

                        if (!userDoc.exists() || !userDoc.data()?.termsAccepted) {
                            console.warn("User missing valid profile. Forcing signout.");
                            sessionStorage.setItem('logoutReason', 'invalid_profile');
                            await auth.signOut();
                            return; // Exit this execution, the signout will trigger a new event
                        }
                    }

                    // Force refresh to get latest claims (especially on signup matching invitation)
                    const tokenResult = await currentUser.getIdTokenResult(true);
                    if (currentCallId !== callId) return; // Prevent stale update
                    
                    setRole((tokenResult.claims.role as string) || null);
                    setSchoolId((tokenResult.claims.schoolId as string) || null);
                    setBoardId((tokenResult.claims.boardId as string) || null);
                } catch (e) {
                    if (currentCallId !== callId) return; // Prevent stale update
                    console.error("Error retrieving custom claims or validating user:", e);
                    setRole(null);
                    setSchoolId(null);
                    setBoardId(null);
                }
            } else {
                setRole(null);
                setSchoolId(null);
                setBoardId(null);
            }

            if (currentCallId !== callId) return; // Prevent stale update

            setUser(currentUser);
            setLoading(false);

            if (currentUser && !previousUser) {
                // User just logged in - check sessionStorage to prevent double logs on state / claim refresh
                const alreadyLoggedSession = sessionStorage.getItem('pp_logged_in_session');
                if (!alreadyLoggedSession) {
                    sessionStorage.setItem('pp_logged_in_session', 'true');
                    sessionStorage.setItem('pp_login_time', Date.now().toString());
                    // Note: The actual 'user_login' audit log is now handled securely on the backend
                    // via the Identity Platform beforeSignIn blocking function (functions/src/auth.ts)
                    // which captures the IP address. We no longer log it from the client side to avoid duplicates.
                }
            } else if (!currentUser && previousUser) {
                // User just logged out (sessionStorage cleanup is done in Logger.logLogoutAndSignOut or tab_close hook)
                sessionStorage.removeItem('pp_logged_in_session');
                sessionStorage.removeItem('pp_login_time');
                sessionStorage.removeItem('pp_initial_redirect_done');
                localStorage.removeItem('activeCourseId');
            }
            previousUser = currentUser;
        });

        return () => {
            callId++; // Invalidate any pending async calls on unmount
            unsubscribe();
        };
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading, role, schoolId, boardId }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
